import React, { useState, useEffect } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function WeeklyChecklistDetails({ title = "Weekly Checklist" }) {
  const navigate = useNavigate();
  const { childId } = useParams();

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [answers, setAnswers] = useState({}); // { [questionId]: "yes" | "not_sure" | "no" }

  useEffect(() => {
    const fetchChecklistData = async () => {
      try {
        setLoading(true);
        const questionsResponse = await api.get(
          `/children/weekly-checklist-questions/by-child/?child_id=${childId}`
        );
        const formattedData = {
          childName: questionsResponse.data.child_name,
          categories: questionsResponse.data.question_sets.map((qs) => ({
            id: qs.id,
            label: qs.name,
            questions: qs.questions.map((q) => ({
              id: q.id,
              text: q.text,
              imageUrl: q.image || null,
            })),
          })),
        };
        setChecklist(formattedData);
        if (formattedData.categories.length > 0) {
          setActiveCategoryId(formattedData.categories[0].id);
        }

        // Fetch previous responses
        const responsesResponse = await api.get(
          `/children/weekly-checklist-responses/current-week/?child_id=${childId}`
        );
        const previousAnswers = {};
        if (responsesResponse.data && Array.isArray(responsesResponse.data)) {
          responsesResponse.data.forEach((resp) => {
            previousAnswers[resp.question] = resp.answer;
          });
        }
        setAnswers(previousAnswers);
      } catch (err) {
        setError("Failed to load checklist. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchChecklistData();
    }
  }, [childId]);

  const activeCategory =
    checklist?.categories.find((c) => c.id === activeCategoryId) ||
    checklist?.categories[0];

  const handleAnswer = async (questionId, value) => {
    // Optimistic UI update
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    try {
      await api.post("/children/weekly-checklist-responses/submit-answer/", {
        child_id: childId,
        question_id: questionId,
        answer: value,
      });
      // Successfully saved
    } catch (err) {
      console.error("Failed to save answer:", err);
      // Optional: Revert state or show an error toast
      // For now, we'll leave the optimistic update
      // Example: setAnswers(answers); // This would revert the change
    }
  };

  const getAnswerStyle = (questionId, option) => {
    const selected = answers[questionId];
    const base =
      "flex-1 py-2 rounded-full text-xs font-medium transition border";

    if (!selected) {
      if (option === "no")
        return `${base} bg-[#1e3a5f] text-white border-[#1e3a5f]`;
      if (option === "yes")
        return `${base} bg-white text-[#1e3a5f] border-[#d0d8e6]`;
      return `${base} bg-white text-gray-600 border-[#d0d8e6]`;
    }

    const isSelected = selected === option;
    if (isSelected) {
      if (option === "yes")
        return `${base} bg-[#25b67e] text-white border-[#25b67e]`;
      if (option === "no")
        return `${base} bg-[#1e3a5f] text-white border-[#1e3a5f]`;
      return `${base} bg-[#fbbf24] text-white border-[#fbbf24]`;
    }

    return `${base} bg-white text-gray-400 border-[#e2e8f0]`;
  };

  const handleNextCategory = () => {
    if (!checklist || !activeCategory) return;

    const currentIndex = checklist.categories.findIndex(
      (c) => c.id === activeCategory.id
    );
    if (currentIndex === -1) return;

    const isLast = currentIndex === checklist.categories.length - 1;

    if (!isLast) {
      const nextCategory = checklist.categories[currentIndex + 1];
      setActiveCategoryId(nextCategory.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(-1); // last category -> go back (or change behaviour)
    }
  };

  const getFooterLabel = () => {
    if (!checklist || !activeCategory) return "Next";
    const currentIndex = checklist.categories.findIndex(
      (c) => c.id === activeCategory.id
    );
    const isLast = currentIndex === checklist.categories.length - 1;
    return isLast ? "Finish" : "Next";
  };

  const getProgressText = () => {
    if (!activeCategory) return "";
    const total = activeCategory.questions.length || 0;
    const answered = activeCategory.questions.filter((q) => answers[q.id])
      .length;
    return `${answered}/${total} answered`;
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <LoadingSpinner />
          <p className="text-gray-500 mt-2">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!checklist || checklist.categories.length === 0) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col min-h-screen">
          <div className="bg-white/70 backdrop-blur-sm px-5 py-4 flex items-center justify-between shadow-sm">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
            <h1 className="text-lg font-semibold text-[#1e3a5f]">{title}</h1>
            <div className="w-8" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">
              No weekly checklist available for this child.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-sm px-4 md:px-8 py-4 flex items-center justify-between shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
              {title}
            </h1>
            <p className="text-xs text-gray-500">
              Weekly check-in for {checklist.childName}
            </p>
          </div>
          <div className="w-8" />
        </div>


        {/* Content */}
        <div className="flex-1 px-4 md:px-6 lg:px-8 mt-12 pt-10 pb-28">
          <div className="max-w-xl mx-auto">
            {/* Category Tabs (top, centered, phone-like) */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3">
              {checklist.categories.map((category) => {
                const isActive = category.id === activeCategory?.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategoryId(category.id);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`whitespace-nowrap px-4 py-2 rounded-2xl text-xs font-medium border transition ${
                      isActive
                        ? "bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-sm"
                        : "bg-white/80 text-[#1e3a5f] border-[#d0d8e6]"
                    }`}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>

            {/* Small progress text under tabs */}
            <p className="text-[11px] text-gray-500 mb-2">{getProgressText()}</p>

            {/* Question cards – one card per question, stacked like screenshot */}
            <div className="space-y-5">
              {activeCategory &&
              activeCategory.questions &&
              activeCategory.questions.length > 0 ? (
                activeCategory.questions.map((q) => (
                  <div
                    key={q.id}
                    className="bg-white rounded-3xl shadow-md overflow-hidden"
                  >
                    {/* Image */}
                    <div className="h-52 w-full overflow-hidden bg-gray-100">
                      {q.imageUrl ? (
                        <img
                          src={q.imageUrl}
                          alt="Weekly checklist"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    {/* Question strip */}
                    <div className="bg-[#ffe5ef] px-4 py-3 text-center">
                      <p className="text-sm text-[#1e3a5f] font-medium leading-snug">
                        {q.text}
                      </p>
                    </div>

                    {/* Answer buttons row */}
                    <div className="bg-white px-4 py-3 flex gap-2">
                      <button
                        className={getAnswerStyle(q.id, "yes")}
                        onClick={() => handleAnswer(q.id, "yes")}
                      >
                        Yes
                      </button>
                      <button
                        className={getAnswerStyle(q.id, "not_sure")}
                        onClick={() => handleAnswer(q.id, "not_sure")}
                      >
                        Not Sure
                      </button>
                      <button
                        className={getAnswerStyle(q.id, "no")}
                        onClick={() => handleAnswer(q.id, "no")}
                      >
                        No
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    No questions in this category.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom footer bar with Next button */}
        <div className="fixed inset-x-0 bottom-0 bg-white/90 backdrop-blur-sm border-t border-[#e5e7eb]">
          <div className="px-4 md:px-6 lg:px-8 py-3">
            <div className="max-w-xl mx-auto flex items-center gap-3">
              <div className="flex-1">
                <p className="text-[11px] text-gray-500">
                  {activeCategory?.label}
                </p>
                <p className="text-xs font-medium text-[#1e3a5f]">
                  {getProgressText()}
                </p>
              </div>
              <button
                onClick={handleNextCategory}
                className="px-8 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold shadow-md w-[40%] md:w-auto"
              >
                {getFooterLabel()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
